import { useRef, useState, type ReactNode } from 'react';

import { Plus, X } from 'lucide-react';

import Text from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { FormSpacing } from '@/theme/size-tokens';

type ArraySchemaFieldProps = {
  /** Rendered label text */
  label: string;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
  /** Current array items as newline-delimited string */
  value: string;
  /** Callback when items change */
  onChange: (value: string) => void;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is read-only */
  disabled?: boolean;
  /** Spacing between label, list, and input */
  spacing?: FormSpacing;
};

/**
 * ArraySchemaField — renders an array field as tags with an input to add items.
 * Items are managed as a newline-delimited string for compatibility with SchemaFieldValue.
 */
export default function ArraySchemaField({
  label,
  labelSuffix,
  value,
  onChange,
  required = false,
  disabled = false,
  spacing = 'md',
}: Readonly<ArraySchemaFieldProps>) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse current items from the newline-delimited value.
  const items = value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  const handleAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed || items.includes(trimmed)) {
      setDraft('');
      return;
    }
    const next = [...items, trimmed].join('\n');
    onChange(next);
    setDraft('');
    inputRef.current?.focus();
  };

  const handleRemove = (index: number) => {
    const next = items.filter((_, i) => i !== index).join('\n');
    onChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    } else if (e.key === 'Backspace' && draft === '' && items.length > 0) {
      e.preventDefault();
      handleRemove(items.length - 1);
    }
  };

  // Remove trailing 's' for singular placeholder.
  const singularLabel = label.endsWith('s') ? label.slice(0, -1) : label;

  const gapClass =
    spacing === 'xs'
      ? 'gap-1.5'
      : spacing === 'sm'
        ? 'gap-2'
        : spacing === 'md'
          ? 'gap-3'
          : 'gap-4';

  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0 sm:gap-2">
      <legend className="text-foreground float-none w-full text-base font-semibold sm:text-sm">
        <span className="inline-flex items-center gap-1.5">
          {label}
          {required && (
            <span className="text-error" aria-hidden="true">
              *
            </span>
          )}
          {labelSuffix}
        </span>
      </legend>

      {/* Input + Add button row */}
      {!disabled && (
        <div className="flex min-w-0 gap-2">
          <input
            ref={inputRef}
            type="text"
            placeholder={`Enter one ${singularLabel.toLowerCase()}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            aria-label={`Add ${singularLabel.toLowerCase()}`}
            className="border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary bg-background flex min-w-0 flex-1 rounded border px-3 py-2 text-sm transition-colors focus:ring-1 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!draft.trim() || items.includes(draft.trim())}
            aria-label={`Add ${singularLabel.toLowerCase()}`}
            className="border-border text-foreground hover:bg-muted bg-background hover:disabled:bg-background inline-flex cursor-pointer items-center justify-center gap-1.5 rounded border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Tags list */}
      {items.length > 0 && (
        <div className={cn('flex flex-wrap', gapClass)}>
          {items.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="border-border bg-muted/40 text-foreground inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm leading-tight"
            >
              <span>{item}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-muted-foreground hover:text-foreground -mr-0.5 ml-0.5 inline-flex cursor-pointer items-center rounded transition-colors"
                  aria-label={`Remove ${item}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {items.length === 0 && disabled && (
        <Text size="sm" variant="muted">
          No {label.toLowerCase()} configured.
        </Text>
      )}
    </fieldset>
  );
}
